import React from 'react';
import PropTypes from 'prop-types';
import {Box, CircularProgress, Stack, Typography} from '@mui/material';
import {DrawerHeader} from '../common/DrawerHeader';
import AiStarIcon from '../../assets/ai-star.svg';
import FootingCheckIcon from '../../assets/footing-check.svg';
import ChatMessageIcon from '../../assets/chat-message.svg';
import InconsistencyIcon from '../../assets/inconsistency.svg';
import {WHITE_100} from '../../theme';
import {STATUS as FOOTING_STATUS} from './hooks/usePdfFootingCheck';
import {STATUS as INCONSISTENCY_STATUS} from './hooks/usePdfInconsistencyCheck';

const styles = {
  content: {
    suggested: {
      color: '#67716E',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '140%',
    },
    suggestedItem: {
      container: {
        borderRadius: '0.5rem',
        border: '1px solid #DDE0DE',
        padding: '0.75rem 1rem',
        backgroundColor: WHITE_100,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: '#F5F5F5',
        },
      },
      containerDisabled: {
        borderRadius: '0.5rem',
        border: '1px solid #DDE0DE',
        padding: '0.75rem 1rem',
        backgroundColor: '#F5F5F5',
        cursor: 'not-allowed',
        opacity: 0.7,
      },
      label: {
        color: '#162523',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: '150%',
      },
      description: {
        color: '#67716E',
        fontSize: '12px',
        fontWeight: 400,
        lineHeight: '150%',
      },
    },
  },
  statusBadge: {
    running: {
      backgroundColor: '#FFF3CD',
      color: '#856404',
      fontSize: '10px',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '4px',
    },
    complete: {
      backgroundColor: '#D4EDDA',
      color: '#155724',
      fontSize: '10px',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '4px',
    },
    error: {
      backgroundColor: '#F8D7DA',
      color: '#721C24',
      fontSize: '10px',
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: '4px',
    },
  },
};

const PDFReviewHome = ({
  onClose,
  onNavigateToFootings,
  onNavigateToChat,
  onNavigateToInconsistencies,
  footingStatus = FOOTING_STATUS.IDLE,
  inconsistencyStatus = INCONSISTENCY_STATUS.IDLE,
  isPdfParsed = false,
  pdfPennyChatEnabled = false,
}) => {
  const isParsingRunning = footingStatus === FOOTING_STATUS.PARSING;
  const isFootingsDisabled = isParsingRunning || !isPdfParsed;
  const isInconsistenciesDisabled = isParsingRunning || !isPdfParsed;
  const isChatDisabled = isParsingRunning || !isPdfParsed;

  return (
    <Stack direction="column" height="100%" flex={1}>
      <DrawerHeader onClose={onClose}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <img src={AiStarIcon} width={20} height={20} alt="AI Assistant" />
          <Typography ml={1} fontSize={20} fontWeight={600}>
            AI Assistant
          </Typography>
        </Stack>
      </DrawerHeader>

      <Stack flex={1} px={3} mt={1.5} mb={2}>
        <Stack direction="column" flex={1} spacing={1.5} mt={1.5}>
          {/* Footing Check Card */}
          <Box
            sx={
              isFootingsDisabled
                ? styles.content.suggestedItem.containerDisabled
                : styles.content.suggestedItem.container
            }
            onClick={isFootingsDisabled ? undefined : onNavigateToFootings}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box>
                <img
                  src={FootingCheckIcon}
                  width={24}
                  height={24}
                  alt="Footing Check"
                />
              </Box>
              <Stack flex={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={styles.content.suggestedItem.label}>
                    Run footing check
                  </Typography>
                </Stack>
                <Typography
                  sx={styles.content.suggestedItem.description}
                  mt={0.5}
                >
                  {!isPdfParsed
                    ? 'Preparing document for review. This may take a few minutes...'
                    : 'Check for incorrect footing calculation.'}
                </Typography>
              </Stack>
              {isFootingsDisabled && (
                <CircularProgress size={16} sx={{color: '#67716E'}} />
              )}
            </Stack>
          </Box>

          {/* Inconsistency Check Card */}
          <Box
            sx={
              isInconsistenciesDisabled
                ? styles.content.suggestedItem.containerDisabled
                : styles.content.suggestedItem.container
            }
            onClick={
              isInconsistenciesDisabled
                ? undefined
                : onNavigateToInconsistencies
            }
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box>
                <img
                  src={InconsistencyIcon}
                  width={24}
                  height={24}
                  alt="Inconsistency Check"
                />
              </Box>
              <Stack flex={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography sx={styles.content.suggestedItem.label}>
                    Run inconsistency check
                  </Typography>
                </Stack>
                <Typography
                  sx={styles.content.suggestedItem.description}
                  mt={0.5}
                >
                  {!isPdfParsed
                    ? 'Preparing document for review. This may take a few minutes...'
                    : 'Check for inconsistent values across the document.'}
                </Typography>
              </Stack>
              {isInconsistenciesDisabled && (
                <CircularProgress size={16} sx={{color: '#67716E'}} />
              )}
            </Stack>
          </Box>

          {/* Chat with Penny Card */}
          {pdfPennyChatEnabled && (
            <Box
              sx={
                isChatDisabled
                  ? styles.content.suggestedItem.containerDisabled
                  : styles.content.suggestedItem.container
              }
              onClick={isChatDisabled ? undefined : onNavigateToChat}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box>
                  <img
                    src={ChatMessageIcon}
                    width={24}
                    height={24}
                    alt="Chat with Penny"
                  />
                </Box>
                <Stack flex={1}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography sx={styles.content.suggestedItem.label}>
                      Chat with Penny
                    </Typography>
                  </Stack>
                  <Typography
                    sx={styles.content.suggestedItem.description}
                    mt={0.5}
                  >
                    {!isPdfParsed
                      ? 'Preparing document for review. This may take a few minutes...'
                      : 'Ask questions about this PDF document.'}
                  </Typography>
                </Stack>
                {isChatDisabled && (
                  <CircularProgress size={16} sx={{color: '#67716E'}} />
                )}
              </Stack>
            </Box>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

PDFReviewHome.propTypes = {
  onClose: PropTypes.func.isRequired,
  onNavigateToFootings: PropTypes.func.isRequired,
  onNavigateToChat: PropTypes.func.isRequired,
  onNavigateToInconsistencies: PropTypes.func.isRequired,
  footingStatus: PropTypes.oneOf(Object.values(FOOTING_STATUS)),
  inconsistencyStatus: PropTypes.oneOf(Object.values(INCONSISTENCY_STATUS)),
  isPdfParsed: PropTypes.bool,
  pdfPennyChatEnabled: PropTypes.bool,
};

export default PDFReviewHome;
